import React, { Dispatch } from "react";
import { Button } from "reactstrap";
import { connect } from "react-redux";
import { css } from "emotion";
import { showSnack } from "react-redux-snackbar";
import { IAppState, LoadingState, ErrorDisplay, ApiRequest } from "../../../Common";
import { ResponseError } from "../../../Common/Models/ResponseError";
import {
  getTopicSubscriptionStatus,
  setTopicSubscriptionStatusLoading,
  unloadTopicSubscriptionStatus
} from "../../Actions/TopicSubscriptionActions";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { StatusCode } from "../../../App/Enums/StatusCode";

type Props = {
  topicCode: string;
  onClick?: () => void;
};

type StateProps = {
  topicName?: string;
  isSubscribed?: boolean;
  loadingState: LoadingState;
  loadingError?: ResponseError;
};

type State = {
  lock: boolean;
};

type DispatchProps = {
  getTopicSubscriptionStatus: (topicCode: string) => void;
  setTopicSubscriptionStatusLoading: (topicCode: string) => void;
  unloadTopicSubscriptionStatus: (topicCode: string) => void;
  showSnack: (uniqueId: any, args: any) => void;
};

const mapStateToProps = (state: IAppState, props: Props): StateProps => {
  if (state.topicSubscriptions.subscriptionStates[props.topicCode]) {
    return {
      topicName: state.topicSubscriptions.subscriptionStates[props.topicCode].topicName,
      isSubscribed: state.topicSubscriptions.subscriptionStates[props.topicCode].isSubscribed,
      loadingState: state.topicSubscriptions.subscriptionStates[props.topicCode].loadingState,
      loadingError: state.topicSubscriptions.subscriptionStates[props.topicCode].loadingError
    };
  }
  return {
    loadingState: {
      errored: false,
      isLoaded: false,
      isLoading: false
    }
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getTopicSubscriptionStatus: (topicCode: string) =>
    dispatch(getTopicSubscriptionStatus(topicCode)),
  setTopicSubscriptionStatusLoading: (topicCode: string) =>
    dispatch(setTopicSubscriptionStatusLoading(topicCode)),
  unloadTopicSubscriptionStatus: (topicCode: string) =>
    dispatch(unloadTopicSubscriptionStatus(topicCode)),
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class SubscribeToTopicButton extends React.Component<Props & StateProps & DispatchProps, State> {
  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.state = {
      lock: false
    };
  }

  claimLock = () => {
    this.setState({
      lock: true
    });
  };

  releaseLock = () => {
    this.setState({
      lock: false
    });
  };

  componentDidMount = () => {
    this.loadIfNeeded();
  };

  componentDidUpdate = () => {
    this.loadIfNeeded();
  };

  componentWillUnmount = () => {
    this.props.unloadTopicSubscriptionStatus(this.props.topicCode);
  };

  loadIfNeeded = () => {
    if (
      !this.props.loadingState.isLoaded &&
      !this.props.loadingState.isLoading &&
      !this.props.loadingState.errored
    ) {
      this.reload();
    }
  };

  reload = () => {
    this.props.setTopicSubscriptionStatusLoading(this.props.topicCode);
    this.props.getTopicSubscriptionStatus(this.props.topicCode);
  };

  subscribe = () => {
    this.claimLock();

    if (this.state.lock) {
      return;
    }

    ApiRequest.fromPath(endpoints.subscriptions.subscribe(this.props.topicCode))
      .withAuthHeader()
      .post()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            this.reload();
          },
          result => {
            this.showErrorSnackbar(response.status, result.error.message);
          },
          () => {
            this.showErrorSnackbar(response.status, "An unknown error has occured");
          }
        ).catch(() => {
          this.showErrorSnackbar(-1, "Failed to parse json response");
        });
      })
      .catch(() => {
        this.showErrorSnackbar(StatusCode.Status503ServiceUnavailable, "Unable to contact server");
      })
      .finally(() => this.releaseLock());
  };

  unsubscribe = () => {
    this.claimLock();

    if (this.state.lock) {
      return;
    }

    ApiRequest.fromPath(endpoints.subscriptions.unsubscribe(this.props.topicCode))
      .withAuthHeader()
      .post()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            this.reload();
          },
          result => {
            this.showErrorSnackbar(response.status, result.error.message);
          },
          () => {
            this.showErrorSnackbar(response.status, "An unknown error has occured");
          }
        ).catch(() => {
          this.showErrorSnackbar(-1, "Failed to parse json response");
        });
      })
      .catch(() => {
        this.showErrorSnackbar(StatusCode.Status503ServiceUnavailable, "Unable to contact server");
      })
      .finally(() => this.releaseLock());
  };

  showErrorSnackbar(errorCode: number, errorDescription: string) {
    this.props.showSnack(`TOPIC_SUBSCRIBE_ERROR_${this.props.topicCode}`, {
      label: `${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  }

  render() {
    if (this.props.loadingState.errored && !!this.props.loadingError) {
      return <ErrorDisplay {...this.props.loadingError} />;
    }

    let text = "Loading...";
    if (!this.props.loadingState.isLoading) {
      if (this.props.isSubscribed) {
        text = "Subscribed";
      } else {
        text = `Subscribe to ${this.props.topicName} feedback`;
      }
    }

    const action = () => {
      if (this.props.onClick) {
        this.props.onClick();
      }
      if (this.props.isSubscribed) {
        this.unsubscribe();
      } else {
        this.subscribe();
      }
    };

    return (
      <Button disabled={this.state.lock || this.props.loadingState.isLoading} onClick={action}>
        {text}
      </Button>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SubscribeToTopicButton);
